import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { LineItemsGrid } from '@iheartjane/menu';
import { Box, Flex, Skeleton, Typography } from '@iheartjane/reefer';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import { Fragment } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useTipSettings } from '@jane/shared-ecomm/hooks';
import { useCheckout } from '@jane/shared-ecomm/providers';

import { useCheckoutPayments } from '../CheckoutPayments/CheckoutPaymentsProvider';
import { DEFAULT_TIP_PAYMENT_METHODS } from '../util/hooks/usePaymentOptions';
import { useReservationModeLabels } from '../util/hooks/useReservationModeLabels';
import { Tipping } from './Tipping';
import { RewardsGrid } from './checkoutSummary.styles';

export const CheckoutTotal = () => {
  const {
    appInfo: { isChangingReservationModes },
    cartLineItems,
    cart: {
      crm_redemptions,
      jane_pay_service_fee_amount,
      reservation_mode,
      tax_included,
    },
    deliveryAddress,
    store: { delivery_tip_settings, tip_settings },
  } = useCheckout();
  const { control } = useFormContext();
  const pickupMode = useWatch({ control, name: 'pickupOptions.pickupMode' });
  const { selected: selectedPaymentMethod } = useCheckoutPayments();
  const showCrmLineItem = useFlag(FLAGS.c3RewardsLineItem);

  const { showTip } = useTipSettings({
    reservationMode: reservation_mode,
    pickupMode,
    deliveryTipSettings: (delivery_tip_settings as any) || undefined,
    tipSettings: (tip_settings as any) || undefined,
    selectedPaymentMethod,
    tipPaymentMethods: DEFAULT_TIP_PAYMENT_METHODS,
  });

  const { deliveryLabel } = useReservationModeLabels(true);
  const { reservationModeLabel } = useReservationModeLabels();

  return (
    <Box>
      <LineItemsGrid
        {...cartLineItems}
        isRecalculating={isChangingReservationModes}
        reservationModeLabel={reservationModeLabel}
        taxIncluded={tax_included}
        hasDeliveryAddress={Boolean(deliveryAddress)}
        deliveryLabel={deliveryLabel}
        reservationMode={reservation_mode}
        janePayServiceFeeAmount={jane_pay_service_fee_amount}
        selectedPaymentMethod={selectedPaymentMethod}
        showTip={showTip}
      />

      {showTip && <Tipping />}

      <Flex flexDirection="row" mt={24} justifyContent="space-between">
        <Typography variant="header-bold" as="p" branded>
          Estimated Total
        </Typography>
        {isChangingReservationModes ? (
          <Skeleton.Bone ml="auto" width={75} />
        ) : (
          <Typography variant="header-bold" as="p" branded>
            {formatCurrency(cartLineItems.total)}
          </Typography>
        )}
      </Flex>

      {showCrmLineItem && (
        <RewardsGrid>
          {crm_redemptions?.map((crm_item) => {
            return (
              <Fragment key={crm_item.crm_reward_id}>
                <Typography>Reward: {crm_item.reward_name}</Typography>
                <Typography textAlign="right">
                  {crm_item.point_value}{' '}
                  {pluralize({
                    noun: 'point',
                    number: crm_item.point_value,
                    returnValue: 'word',
                  })}
                </Typography>
              </Fragment>
            );
          })}
        </RewardsGrid>
      )}
    </Box>
  );
};
